import React, { useEffect, useState, useCallback } from "react";
import {
  UserPlus,
  BarChart3,
  Flag,
  ShieldAlert,
  TrendingUp,
  Loader2,
  AlertTriangle,
  Globe2,
  Users,
  Coins,
  Check,
  ArrowRight,
  Landmark,
  ArrowDownRight,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { fmtVND, fmtDateTime } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

export default function AdminFinance() {
  return (
    <div className="space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-orange-600 font-semibold">
          Finance Operations
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">
          Corporate Finance Console
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Onboard new customers, monitor payout analytics, review AML red flags,
          and manage cross-border risk exposure.
        </p>
      </header>

      <Tabs defaultValue="onboarding" className="space-y-6">
        <TabsList
          data-testid="finance-tabs"
          className="grid grid-cols-2 sm:grid-cols-5 gap-1 bg-slate-100 p-1 rounded-full w-full sm:w-fit"
        >
          <TabsTrigger
            data-testid="tab-onboarding"
            value="onboarding"
            className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
          >
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger
            data-testid="tab-analytics"
            value="analytics"
            className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
          >
            <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
            Analytics
          </TabsTrigger>
          <TabsTrigger
            data-testid="tab-redflags"
            value="redflags"
            className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
          >
            <Flag className="h-3.5 w-3.5 mr-1.5" />
            Red Flags
          </TabsTrigger>
          <TabsTrigger
            data-testid="tab-risk"
            value="risk"
            className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
          >
            <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
            Risk
          </TabsTrigger>
          <TabsTrigger
            data-testid="tab-thunes"
            value="thunes"
            className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
          >
            <Landmark className="h-3.5 w-3.5 mr-1.5" />
            Thunes USD
          </TabsTrigger>
        </TabsList>

        <TabsContent value="onboarding">
          <OnboardingPane />
        </TabsContent>
        <TabsContent value="analytics">
          <AnalyticsPane />
        </TabsContent>
        <TabsContent value="redflags">
          <RedFlagsPane />
        </TabsContent>
        <TabsContent value="risk">
          <RiskPane />
        </TabsContent>
        <TabsContent value="thunes">
          <ThunesAccountPane />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================================================
   ONBOARDING
   ============================================================ */
function OnboardingPane() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    email: "",
    starting_balance_vnd: "",
    kyc_tier: "basic",
    country: "VN",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const c = await api.clients();
    setClients(c);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      await api.onboardClient({
        ...form,
        starting_balance_vnd: parseInt(form.starting_balance_vnd || "0", 10) || 0,
      });
      toast.success(`${form.name} onboarded successfully`);
      setForm({ name: "", email: "", starting_balance_vnd: "", kyc_tier: "basic", country: "VN", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      {/* Form */}
      <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <UserPlus className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">
              Onboard new customer
            </h3>
            <p className="text-xs text-slate-500">Mocked KYC — no external checks.</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Full name</Label>
            <Input
              data-testid="onboard-name"
              placeholder="Jane Nguyen"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              data-testid="onboard-email"
              placeholder="jane@example.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">KYC tier</Label>
              <Select
                value={form.kyc_tier}
                onValueChange={(v) => setForm({ ...form, kyc_tier: v })}
              >
                <SelectTrigger data-testid="onboard-kyc"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="enhanced">Enhanced Due Diligence</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Country</Label>
              <Select
                value={form.country}
                onValueChange={(v) => setForm({ ...form, country: v })}
              >
                <SelectTrigger data-testid="onboard-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="VN">Vietnam</SelectItem>
                  <SelectItem value="SG">Singapore</SelectItem>
                  <SelectItem value="TH">Thailand</SelectItem>
                  <SelectItem value="PH">Philippines</SelectItem>
                  <SelectItem value="ID">Indonesia</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Onboarding credit (VND, optional)</Label>
            <Input
              data-testid="onboard-balance"
              placeholder="e.g., 500000"
              inputMode="numeric"
              value={form.starting_balance_vnd}
              onChange={(e) =>
                setForm({ ...form, starting_balance_vnd: e.target.value.replace(/\D/g, "") })
              }
            />
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Textarea
              data-testid="onboard-note"
              placeholder="Optional context, referral source, etc."
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </div>
          <button
            data-testid="onboard-submit"
            onClick={submit}
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-teal-700 py-3 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Onboard customer
          </button>
        </div>
      </div>

      {/* Client roster */}
      <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">
              Client roster
            </h3>
            <p className="text-xs text-slate-500">
              {loading ? "Loading…" : `${clients.length} customers on the platform`}
            </p>
          </div>
          <Users className="h-5 w-5 text-slate-400" />
        </div>
        <div className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto">
          {clients.map((c) => (
            <div
              key={c.id}
              data-testid={`client-row-${c.id}`}
              className="flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-700">
                {c.name.split(" ").slice(0, 2).map((s) => s[0]).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-900 truncate">{c.name}</p>
                  <span
                    className={`pill ${
                      c.kyc_tier === "enhanced"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : c.kyc_tier === "verified"
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-slate-100 text-slate-700 border-slate-200"
                    }`}
                  >
                    KYC · {c.kyc_tier}
                  </span>
                  <span className="pill bg-slate-100 text-slate-700 border-slate-200">
                    <Globe2 className="h-3 w-3" /> {c.country}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {c.email} • {c.payouts_count} payouts
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-display font-bold text-slate-900 tabular-nums">
                  {fmtVND(c.balance_vnd)}
                </p>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">
                  balance
                </p>
              </div>
            </div>
          ))}
          {!loading && clients.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              No clients yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   ANALYTICS
   ============================================================ */
function AnalyticsPane() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.analytics().then(setData);
  }, []);

  if (!data)
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading analytics…
      </div>
    );

  return (
    <div className="space-y-6" data-testid="analytics-pane">
      {/* Headline KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Metric
          label="Total payouts processed"
          value={data.total_payouts.toString()}
          icon={<TrendingUp className="h-4 w-4 text-teal-700" />}
          testId="metric-total-payouts"
        />
        <Metric
          label="Success rate"
          value={
            data.success_rate == null
              ? "—"
              : `${(data.success_rate * 100).toFixed(1)}%`
          }
          icon={<Check className="h-4 w-4 text-emerald-600" />}
          testId="metric-success-rate"
        />
        <Metric
          label="Fee revenue (completed)"
          value={fmtVND(data.total_fee_revenue_vnd)}
          icon={<Coins className="h-4 w-4 text-orange-600" />}
          testId="metric-fee-revenue"
        />
      </div>

      {/* Currency pair breakdown */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">
          Currency pair volume
        </h3>
        {data.currency_pairs.length === 0 ? (
          <p className="text-sm text-slate-500">No payouts yet.</p>
        ) : (
          <div className="space-y-4">
            {data.currency_pairs.map((cp) => (
              <div key={cp.pair} data-testid={`ccy-pair-${cp.pair}`}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-sm font-bold text-slate-900">
                      {cp.pair}
                    </span>
                    <span className="text-xs text-slate-500">
                      {cp.count} payouts
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 tabular-nums">
                    {fmtVND(cp.gross_vnd)}
                  </div>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${
                      cp.pair.endsWith("USD") ? "bg-orange-500" : "bg-teal-700"
                    }`}
                    style={{ width: `${cp.share * 100}%` }}
                  />
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {(cp.share * 100).toFixed(1)}% share • {fmtVND(cp.fee_vnd)} fees collected
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Status + Top customers */}
      <div className="grid md:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-display text-lg font-bold text-slate-900 mb-4">
            Status breakdown
          </h3>
          <div className="space-y-2">
            {data.status_breakdown.map((s) => (
              <div
                key={s.status}
                data-testid={`status-row-${s.status}`}
                className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2"
              >
                <span className="text-sm capitalize text-slate-700">
                  {s.status.replace(/_/g, " ")}
                </span>
                <div className="text-right">
                  <p className="text-sm font-semibold tabular-nums text-slate-900">
                    {s.count}
                  </p>
                  <p className="text-[10px] text-slate-500 tabular-nums">
                    {fmtVND(s.amount_vnd)}
                  </p>
                </div>
              </div>
            ))}
            {data.status_breakdown.length === 0 && (
              <p className="text-sm text-slate-500">No payouts yet.</p>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-display text-lg font-bold text-slate-900 mb-4">
            Top customers by volume
          </h3>
          <div className="space-y-3">
            {data.top_customers.length === 0 && (
              <p className="text-sm text-slate-500">No payouts yet.</p>
            )}
            {data.top_customers.map((c, i) => (
              <div
                key={c.user_id}
                data-testid={`top-customer-${i}`}
                className="flex items-center gap-3"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-50 text-teal-700 font-bold text-xs">
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {c.name}
                  </p>
                  <p className="text-[11px] text-slate-500">{c.count} payouts</p>
                </div>
                <p className="text-sm font-semibold text-slate-900 tabular-nums">
                  {fmtVND(c.gross_vnd)}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

const Metric = ({ label, value, icon, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-slate-200 bg-white p-5"
  >
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
      {icon}
      {label}
    </div>
    <p className="mt-2 kpi-num text-3xl text-slate-900 tabular-nums">{value}</p>
  </div>
);

/* ============================================================
   RED FLAGS
   ============================================================ */
function RedFlagsPane() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.redFlags().then(setData);
  }, []);

  if (!data)
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Scanning payouts…
      </div>
    );

  const sevMap = {
    high: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
    medium: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
    low: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500" },
  };

  return (
    <div className="space-y-6" data-testid="redflags-pane">
      <div className="grid grid-cols-3 gap-4">
        <SevCard label="High severity" count={data.counts.high} color="red" testId="sev-high" />
        <SevCard label="Medium severity" count={data.counts.medium} color="amber" testId="sev-medium" />
        <SevCard label="Low severity" count={data.counts.low} color="blue" testId="sev-low" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">
              Flagged activity
            </h3>
            <p className="text-xs text-slate-500">
              {data.total} flag{data.total === 1 ? "" : "s"} detected across all payouts
            </p>
          </div>
          <Flag className="h-5 w-5 text-slate-400" />
        </div>
        <div className="divide-y divide-slate-100 max-h-[540px] overflow-y-auto">
          {data.flags.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">
              No red flags detected. 🎉
            </div>
          )}
          {data.flags.map((f) => {
            const s = sevMap[f.severity];
            return (
              <div
                key={f.id}
                data-testid={`flag-${f.id}`}
                className={`flex items-start gap-3 px-5 py-4 hover:bg-slate-50`}
              >
                <div
                  className={`h-9 w-9 shrink-0 rounded-full ${s.bg} ${s.border} border flex items-center justify-center`}
                >
                  <AlertTriangle className={`h-4 w-4 ${s.text}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`pill ${s.bg} ${s.border} ${s.text}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                      {f.severity.toUpperCase()}
                    </span>
                    <span className="text-sm font-semibold text-slate-900">
                      {f.rule}
                    </span>
                    <span className="text-xs text-slate-500">— {f.user_name}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{f.detail}</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {fmtDateTime(f.created_at)}
                    {f.amount_vnd != null && (
                      <> · <span className="tabular-nums">{fmtVND(f.amount_vnd)}</span></>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const SevCard = ({ label, count, color, testId }) => {
  const map = {
    red: "border-red-200 bg-red-50 text-red-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-5 ${map[color]}`}
    >
      <p className="text-xs uppercase tracking-widest font-semibold opacity-80">
        {label}
      </p>
      <p className="mt-2 kpi-num text-4xl tabular-nums">{count}</p>
    </div>
  );
};

/* ============================================================
   RISK
   ============================================================ */
function RiskPane() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.risk().then(setData);
  }, []);

  if (!data)
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading risk metrics…
      </div>
    );

  const bandColor = {
    low: "text-emerald-700 bg-emerald-50 border-emerald-200",
    moderate: "text-amber-700 bg-amber-50 border-amber-200",
    elevated: "text-red-700 bg-red-50 border-red-200",
  }[data.risk_band];

  return (
    <div className="space-y-6" data-testid="risk-pane">
      {/* Risk score card */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Composite risk score
            </p>
            <div className="mt-2 flex items-baseline gap-3">
              <span
                data-testid="risk-score"
                className="balance-hero text-6xl text-slate-900 tabular-nums"
              >
                {data.risk_score}
              </span>
              <span className="text-lg text-slate-400 font-medium">/ 100</span>
            </div>
            <span
              data-testid="risk-band"
              className={`pill mt-3 ${bandColor} uppercase tracking-widest`}
            >
              <ShieldAlert className="h-3 w-3" />
              {data.risk_band}
            </span>
          </div>
          <div className="flex-1 min-w-[220px] max-w-md">
            <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full ${
                  data.risk_band === "low"
                    ? "bg-emerald-500"
                    : data.risk_band === "moderate"
                    ? "bg-amber-500"
                    : "bg-red-500"
                }`}
                style={{ width: `${Math.min(100, data.risk_score)}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[11px] uppercase tracking-widest text-slate-400">
              <span>Safe</span>
              <span>Elevated</span>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Weighted from reversal ratio, concentration, and cross-border exposure.
            </p>
          </div>
        </div>
      </div>

      {/* Risk KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <RiskCell
          label="Total in-flight exposure"
          value={fmtVND(data.total_exposure_vnd)}
          hint="Sum of pending/processing payouts"
          testId="risk-exposure"
        />
        <RiskCell
          label="Cross-border exposure"
          value={fmtVND(data.cross_border_exposure_vnd)}
          hint={`${data.cross_border_count} USD payouts`}
          testId="risk-xborder"
        />
        <RiskCell
          label="Reversal ratio"
          value={`${(data.reversal_ratio * 100).toFixed(1)}%`}
          hint="Failed + rejected ÷ all payouts"
          testId="risk-reversal"
        />
        <RiskCell
          label="Top concentration"
          value={`${(data.concentration_top_share * 100).toFixed(1)}%`}
          hint={data.concentration_top_name}
          testId="risk-concentration"
        />
      </div>

      {/* Guidance */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-3">
          Recommended actions
        </h3>
        <ul className="space-y-2 text-sm text-slate-700">
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" />
            {data.reversal_ratio > 0.1
              ? "Reversal ratio above 10% — review failure root causes with rail provider."
              : "Reversal ratio within tolerance."}
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" />
            {data.concentration_top_share > 0.5
              ? "Single customer >50% of ledger — consider diversification and enhanced KYC review."
              : "Customer concentration within safe range."}
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" />
            {data.cross_border_exposure_vnd > 5_000_000
              ? "Cross-border USD exposure elevated — verify Thunes coverage & FX hedge."
              : "Cross-border exposure nominal."}
          </li>
        </ul>
      </div>
    </div>
  );
}

const RiskCell = ({ label, value, hint, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-slate-200 bg-white p-5"
  >
    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
      {label}
    </p>
    <p className="mt-2 kpi-num text-2xl text-slate-900 tabular-nums">{value}</p>
    <p className="mt-1 text-[11px] text-slate-500 truncate">{hint}</p>
  </div>
);


/* ============================================================
   THUNES USD NOSTRO ACCOUNT
   ============================================================ */
function ThunesAccountPane() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.thunesAccount().then(setData);
  }, []);

  if (!data)
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Thunes account…
      </div>
    );

  return (
    <div className="space-y-6" data-testid="thunes-pane">
      {/* Hero balance card */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
              <Landmark className="h-4 w-4 text-teal-700" />
              {data.provider} · {data.account_type}
            </div>
            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
              <span
                data-testid="thunes-available"
                className="balance-hero text-5xl sm:text-6xl md:text-7xl text-slate-900 tabular-nums"
              >
                ${data.available_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="text-sm text-slate-500 font-medium">USD available</span>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Account ref:{" "}
              <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
                {data.account_reference}
              </code>
            </p>
            {data.is_low && (
              <div
                data-testid="thunes-low-alert"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Balance below ${data.low_threshold_usd.toLocaleString()} — schedule a top-up wire
              </div>
            )}
          </div>

          <div className="flex-1 min-w-[220px] max-w-md">
            <div className="flex justify-between text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">
              <span>Utilisation</span>
              <span className="tabular-nums text-slate-700 font-semibold">
                {data.utilisation_pct.toFixed(1)}%
              </span>
            </div>
            <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full ${
                  data.utilisation_pct > 80
                    ? "bg-red-500"
                    : data.utilisation_pct > 50
                    ? "bg-amber-500"
                    : "bg-teal-700"
                }`}
                style={{ width: `${Math.min(100, data.utilisation_pct)}%` }}
              />
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Debited + held vs. prefunded balance.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-6 border-t border-slate-100">
          <NostroCell
            label="Prefunded"
            value={`$${data.prefund_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            testId="thunes-prefund"
          />
          <NostroCell
            label="Debited (settled)"
            value={`-$${data.debited_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            hint={`${data.completed_count} completed`}
            tone="debit"
            testId="thunes-debited"
          />
          <NostroCell
            label="Held (in-flight)"
            value={`$${data.held_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            hint={`${data.inflight_count} in-flight`}
            tone="hold"
            testId="thunes-held"
          />
          <NostroCell
            label="Low threshold"
            value={`$${data.low_threshold_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            testId="thunes-threshold"
          />
        </div>
      </div>

      {/* Transaction log */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">
              Account transactions
            </h3>
            <p className="text-xs text-slate-500">
              Most recent activity on the Thunes USD nostro account.
            </p>
          </div>
          <span className="pill bg-slate-100 text-slate-700 border-slate-200">
            {data.transactions.length} entries
          </span>
        </div>
        <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
          {data.transactions.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              No activity yet.
            </div>
          )}
          {data.transactions.map((t) => {
            const isCredit = t.direction === "credit";
            return (
              <div
                key={t.id}
                data-testid={`thunes-tx-${t.id}`}
                className="flex items-center gap-3 px-5 py-4 hover:bg-slate-50"
              >
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                    isCredit
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {isCredit ? (
                    <ArrowDownRight className="h-4 w-4" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {t.description}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{fmtDateTime(t.created_at)}</span>
                    {t.reference && (
                      <>
                        <span>·</span>
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
                          {t.reference}
                        </code>
                      </>
                    )}
                  </p>
                </div>
                <div
                  className={`shrink-0 font-display text-base font-semibold tabular-nums ${
                    isCredit ? "text-emerald-700" : "text-slate-900"
                  }`}
                >
                  {isCredit ? "+" : "−"}$
                  {t.amount_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const NostroCell = ({ label, value, hint, tone, testId }) => (
  <div data-testid={testId}>
    <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
      {label}
    </p>
    <p
      className={`mt-1 font-display text-xl font-bold tabular-nums ${
        tone === "debit" ? "text-slate-700" : tone === "hold" ? "text-amber-700" : "text-slate-900"
      }`}
    >
      {value}
    </p>
    {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
  </div>
);
